import React from 'react'
import { Grid, Typography, Paper, CircularProgress } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { dateFormat } from '../utils/formatter'
import NumberFormat from 'react-number-format'

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1
  },
  paper: {
    padding: theme.spacing(2),
    color: 'rgba(0,0,0,0.9)',
    height: '100%'
  },
  descript: { fontSize: '0.8rem', color: '#545454' },
  descriptVal: { fontSize: '0.85rem' },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4)
  }
}))

export default function AbsenceManagementMain(props) {
  const classes = useStyles()
  const { data, isLoading } = props

  const printValue = (lookup, description, secondary, secondType) => {
    const isDate = description.toLowerCase().indexOf('date') !== -1
    return (
      <div>
        <span className={classes.descript}>{`${description}:  `}</span>
        <span className={classes.descriptVal}>
          {`${isDate && data[lookup] ? dateFormat(data[lookup]) : data[lookup] || ''}${
            secondary && data[secondary]
              ? ` (${
                  secondType !== 'date'
                    ? data[secondary]
                    : dateFormat(data[secondary])
                })`
              : ''
          } `}
        </span>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    )
  }

  // Check if data is empty or undefined
  const hasData = data && Object.keys(data).length > 0

  if (!hasData) {
    return (
      <div className={classes.loadingContainer}>
        <Typography variant="subtitle1">No data available</Typography>
      </div>
    )
  }

  return (
    <div className={classes.root}>
      <Grid
        container
        spacing={1}
        direction='row'
        justifyContent='flex-start'
        alignItems='stretch'
      >
        {/* Dates */}
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='h5'>Dates</Typography>
            <Grid
              container
              spacing={0}
              direction='row'
              justifyContent='space-evenly'
              alignItems='flex-start'
            >
              <Grid item xs={12} sm={6} lg={6}>
                {printValue('MASTR_ENTRY', 'Master Entry Date')}
                {printValue('MSTR_ELIG_END', 'Master Eligibility End')}
                {printValue('MSTR_END', 'Master End Date')}
                {printValue('TRANSFER_FLAG', 'Transfer Flag')}
                {printValue('MSTR_OVERRIDE', 'Master Override')}
                {printValue('SRV_BEGIN_DT', 'Service Begin Date')}
                {printValue('ACCR_BEGIN_DT', 'Accrual Begin Date')}
                {printValue('ACCR_START_DT', 'Accrual Start Date')}
                {printValue('ACCR_LAST_DT', 'Accrual Last Date')}
              </Grid>
              <Grid item xs={12} sm={6} lg={6}>
                {printValue('ALLOT_BEGIN_DT', 'Allotment Begin Date')}
                {printValue('ALLOT_LAST_DT', 'Allotment Last Date')}
                {printValue('ALLOT_POINT', 'Allotment Point')}
                {printValue('ELIG_BEGIN_DT', 'Eligibility Begin Date')}
                {printValue('ELIG_LAST_DT', 'Eligibility Last Date')}
                {printValue('RESET_POINT', 'Reset Point')}
                {printValue('RESET_LAST_DT', 'Reset Last Date')}
                {printValue('LST_PER_DT_HRS', 'Last Period Date Hours')}
                {printValue('LST_PER_DT_ERN', 'Last Period Date Earnings')}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Balances */}
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='h5'>Balances</Typography>
            <Grid
              container
              spacing={0}
              direction='row'
              justifyContent='space-evenly'
              alignItems='flex-start'
            >
              <Grid item xs={12} sm={6} lg={6}>
                <Typography variant='subtitle1'>Hours</Typography>
                {printValue('AVAIL_HRS_BAL', 'Available Hours Balance')}
                {printValue('ACCR_HRS_BAL', 'Accrued Hours Balance')}
                {printValue('ALLOT_HRS_BAL', 'Allotment Hours Balance')}
                {printValue('RESRV_HRS_BAL', 'Reserved Hours Balance')}
              </Grid>
              <Grid item xs={12} sm={6} lg={6}>
                <Typography variant='subtitle1'>Earnings</Typography>
                {printValue('AVAIL_ERN_BAL', 'Available Earnings Balance')}
                {printValue('ACCR_ERN_BAL', 'Accrued Earnings Balance')}
                {printValue('ALLOT_ERN_BAL', 'Allotment Earnings Balance')}
                {printValue('RESRV_ERN_BAL', 'Reserved Earnings Balance')}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Payroll */}
        <Grid item xs={12}>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='h5'>Payroll</Typography>
            <Grid
              container
              spacing={0}
              direction='row'
              justifyContent='flex-start'
              alignItems='flex-start'
            >
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle1'>Hours</Typography>
                {printValue('OV_HRS_NEG_LMT', 'Override Hours Negative Limit')}
                {printValue('OV_HRS_LINK_TB', 'Override Hours Link TB')}
                {printValue('OV_HRS_PRO_ORD', 'Override Hours Processing Order')}
                {printValue('OV_HRS_NEG_BAL', 'Override Hours Negative Balance')}
                {printValue('OV_HRS_NEG_WEB', 'Override Hours Negative Web')}
                {printValue('OV_HRS_UNPD_CD', 'Override Hours Unpaid Code')}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle1'>Earnings</Typography>
                {printValue('OV_ERN_NEG_LMT', 'Override Earnings Negative Limit')}
                {printValue('OV_ERN_LINK_TB', 'Override Earnings Link TB')}
                {printValue('OV_ERN_PRO_ORD', 'Override Earnings Processing Order')}
                {printValue('OV_ERN_NEG_BAL', 'Override Earnings Negative Balance')}
                {printValue('OV_ERN_NEG_WEB', 'Override Earnings Negative Web')}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* GL Accounts */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='h5'>GL Accounts</Typography>
            <Grid
              container
              spacing={0}
              direction='row'
              justifyContent='flex-start'
              alignItems='flex-start'
            >
              <Grid item xs={12} md={6}>
                <Typography variant='subtitle1'>Hours</Typography>
                {printValue('HRS_ACCR_CO', 'Hours Accrual Company')}
                {printValue('HRS_ACCR_UNIT', 'Hours Accrual Unit')}
                {printValue('HRS_ACCR_ACCT', 'Hours Accrual Account')}
                {printValue('HRS_ACCR_SUB', 'Hours Accrual Sub')}
                {printValue('HRS_EXP_CO', 'Hours Expense Company')}
                {printValue('HRS_EXP_UNIT', 'Hours Expense Unit')}
                {printValue('HRS_EXP_ACCT', 'Hours Expense Account')}
                {printValue('HRS_ACTIVITY', 'Hours Activity')}
                {printValue('HRS_ACCT_CAT', 'Hours Account Category')}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant='subtitle1'>Earnings</Typography>
                {printValue('ERN_ACCR_CO', 'Earnings Accrual Company')}
                {printValue('ERN_ACCR_UNIT', 'Earnings Accrual Unit')}
                {printValue('ERN_ACCR_ACCT', 'Earnings Accrual Account')}
                {printValue('ERN_ACCR_SUB', 'Earnings Accrual Sub')}
                {printValue('ERN_EXP_CO', 'Earnings Expense Company')}
                {printValue('ERN_EXP_UNIT', 'Earnings Expense Unit')}
                {printValue('ERN_EXP_ACCT', 'Earnings Expense Account')}
                {printValue('ERN_ACTIVITY', 'Earnings Activity')}
                {printValue('ERN_ACCT_CAT', 'Earnings Account Category')}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Override Tables */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='h5'>Override Tables</Typography>
            <Grid
              container
              spacing={0}
              direction='row'
              justifyContent='flex-start'
              alignItems='flex-start'
            >
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle1'>Hours</Typography>
                {printValue('OV_ACCR_HRS', 'Override Accrual Hours')}
                {printValue('OV_ALLOT_HRS', 'Override Allotment Hours')}
                {printValue('OV_BAL_HRS', 'Override Balance Hours')}
                {printValue('OV_ACRLIM_HRS', 'Override Accrual Limit Hours')}
                {printValue('OV_CARRY_HRS_T', 'Override Carryover Hours T')}
                {printValue('OV_HRS_LIA', 'Override Hours Liability')}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle1'>Earnings</Typography>
                {printValue('OV_ACCR_ERN', 'Override Accrual Earnings')}
                {printValue('OV_ALLOT_ERN', 'Override Allotment Earnings')}
                {printValue('OV_BAL_ERN', 'Override Balance Earnings')}
                {printValue('OV_ACRLIM_ERN', 'Override Accrual Limit Earnings')}
                {printValue('OV_CARRY_ERN_T', 'Override Carryover Earnings T')}
                {printValue('OV_ERN_LIA', 'Override Earnings Liability')}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Carryover */}
        <Grid item xs={12}>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='h5'>Carryover</Typography>
            <Grid
                container
                spacing={0}
                direction='row'
                justifyContent='space-evenly'
                alignItems='flex-start'
            >
              <Grid item xs={12}>
                {printValue('CARRYOVER_END', 'Carryover End')}
                {printValue('CARRY_HRS_BAL', 'Carryover Hours Balance')}
                {printValue('CARRY_ERN_BAL', 'Carryover Earnings Balance')}
                {printValue('OV_CARRY_HRS_A', 'Override Carryover Hours A')}
                {printValue('OV_CARRY_ERN_A', 'Override Carryover Earnings A')}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

      </Grid>
    </div>
  )
}